import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { Plus, X, Trophy, FilePdf } from "@phosphor-icons/react";

export default function Compare() {
  const [countries, setCountries] = useState([]);
  const [params, setParams] = useSearchParams();
  const initial = (params.get("slugs") || "canada,uae").split(",").filter(Boolean);
  const [selected, setSelected] = useState(initial);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/countries").then((r) => setCountries(r.data)); }, []);

  useEffect(() => {
    if (selected.length === 0) { setResult(null); return; }
    api.get(`/comparison?slugs=${selected.join(",")}`).then((r) => setResult(r.data));
    setParams({ slugs: selected.join(",") }, { replace: true });
  }, [selected]);

  const toggle = (slug) => {
    setSelected((cur) => cur.includes(slug) ? cur.filter((s) => s !== slug) : (cur.length >= 4 ? cur : [...cur, slug]));
  };

  const exportPdf = () => {
    if (!selected.length) return;
    window.open(`${BACKEND_URL}/api/comparison/pdf?slugs=${selected.join(",")}`, "_blank");
  };

  return (
    <div data-testid="compare-page" className="fade-in py-20">
      <div className="container-gos">
        <div className="overline mb-3">Compare</div>
        <h1 className="font-display text-4xl sm:text-5xl text-gos-charcoal tracking-tight">Side-by-side country comparison</h1>
        <p className="text-gos-slate mt-4 max-w-2xl leading-relaxed">Pick up to four countries and review tax, ease of doing business, banking difficulty, and industry strengths.</p>

        <div className="mt-10 flex flex-wrap gap-2 items-center">
          {countries.map((c) => {
            const active = selected.includes(c.slug);
            return (
              <button
                key={c.slug}
                onClick={() => toggle(c.slug)}
                data-testid={`compare-toggle-${c.slug}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm border text-sm transition-colors ${active ? "bg-gos-navy text-white border-gos-navy" : "bg-white border-gos-border text-gos-charcoal hover:border-gos-navy"}`}
              >
                <span>{c.flag}</span>
                <span>{c.name}</span>
                {active ? <X size={12} weight="bold" /> : <Plus size={12} weight="bold" />}
              </button>
            );
          })}
          <button onClick={exportPdf} data-testid="compare-export-pdf-btn" disabled={!selected.length}
            className="btn-secondary text-xs !py-2 !px-3 ml-auto disabled:opacity-50">
            <FilePdf size={14} weight="bold" /> Export PDF
          </button>
        </div>

        {result && result.countries.length > 0 && (
          <div className="mt-12 overflow-x-auto border border-gos-border bg-white rounded-sm" data-testid="comparison-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gos-border bg-gos-subtle">
                  <th className="text-left p-4 font-medium text-gos-navy w-64">Metric</th>
                  {result.countries.map((c) => (
                    <th key={c.slug} className="text-left p-4 font-medium text-gos-navy">
                      <div className="flex items-center gap-2"><span className="text-xl">{c.flag}</span> {c.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.metrics.map((m) => {
                  const values = result.countries.map((c) => c[m.key]);
                  const best = m.lower_better ? Math.min(...values) : Math.max(...values);
                  return (
                    <tr key={m.key} className="border-b border-gos-border last:border-0">
                      <td className="p-4 text-gos-slate">{m.label}</td>
                      {result.countries.map((c) => {
                        const v = c[m.key];
                        const isBest = v === best && result.countries.length > 1;
                        return (
                          <td key={c.slug} className="p-4 font-mono">
                            <div className="flex items-center gap-2">
                              <span className="text-gos-charcoal">
                                {m.prefix || ""}{typeof v === "number" ? v.toLocaleString() : v}{m.suffix || ""}{m.score ? "/10" : ""}
                              </span>
                              {isBest && <Trophy size={14} weight="duotone" className="text-gos-sand" />}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                <tr className="border-t border-gos-border">
                  <td className="p-4 text-gos-slate">Recommended industries</td>
                  {result.countries.map((c) => (
                    <td key={c.slug} className="p-4 text-xs">
                      {(c.recommended_industries || []).map((i) => (
                        <span key={i} className="inline-block bg-gos-subtle text-gos-charcoal px-2 py-0.5 mr-1 mb-1 rounded-sm">{i}</span>
                      ))}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {result?.countries.length === 0 && (
          <div className="mt-12 text-gos-muted">Select at least one country to compare.</div>
        )}
      </div>
    </div>
  );
}
